<template>
  <div class="room-list-container">
    <h2>房间列表</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索房间号或描述"
          style="width: 300px; margin-right: 10px;"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <el-icon><search /></el-icon>
          </template>
        </el-input>
        <el-button type="primary" @click="handleCreate">
          <el-icon><plus /></el-icon> 添加房间
        </el-button>
      </div>
      
      <el-table :data="rooms" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="room_number" label="房间号" />
        <el-table-column prop="type" label="类型" width="100">
          <template #default="scope">
            {{ getTypeName(scope.row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)">
              {{ getStatusName(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="price" label="价格" />
        <el-table-column prop="capacity" label="容纳人数" width="100" />
        <el-table-column prop="floor" label="楼层" width="80" />
        <el-table-column prop="description" label="描述" show-overflow-tooltip />
        <el-table-column label="设施" width="150">
          <template #default="scope">
            <el-tag v-for="amenity in (scope.row.amenities || [])" :key="amenity" size="small" style="margin-right: 5px; margin-bottom: 5px;">
              {{ amenity }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetails(scope.row)">
              <el-icon><View /></el-icon> 查看详情
            </el-button>
            <el-button type="success" size="small" @click="handleEdit(scope.row)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">
              <el-icon><Delete /></el-icon> 删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, View, Edit, Delete } from '@element-plus/icons-vue';
import service from '../../api/axios';

export default {
  name: 'RoomList',
  components: {
    Search,
    Plus,
    View,
    Edit,
    Delete
  },
  setup() {
    const router = useRouter();
    
    const rooms = ref([]);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    
    // 获取房间列表
    const fetchRooms = async () => {
      try {
        const response = await service({
          url: '/manage/rooms',
          method: 'get',
          params: {
            page: currentPage.value,
            pageSize: pageSize.value,
            search: searchQuery.value
          }
        });
        rooms.value = response.data.data;
        total.value = response.data.total;
      } catch (error) {
        ElMessage.error('获取房间列表失败');
        console.error(error);
      }
    };
    
    // 页面加载时获取房间列表
    onMounted(() => {
      fetchRooms();
    });
    
    // 搜索房间
    const handleSearch = () => {
      fetchRooms();
    };
    
    // 跳转到添加房间页面
    const handleCreate = () => {
      router.push('/rooms/create');
    };
    
    // 查看房间详情
    const handleViewDetails = (room) => {
      router.push({
        path: `/rooms/detail/${room.id}`
      });
    };
    
    // 跳转到编辑房间页面
    const handleEdit = (room) => {
      router.push({
        path: `/rooms/edit/${room.id}`
      });
    };
    
    // 删除房间
    const handleDelete = (room) => {
      ElMessageBox.confirm('确定要删除该房间吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await service({
            url: `/manage/rooms/${room.id}`,
            method: 'delete'
          });
          ElMessage.success('删除成功');
          fetchRooms();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchRooms();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchRooms();
    };
    
    // 获取类型名称
    const getTypeName = (type) => {
      const typeMap = {
        'single': '单人间',
        'double': '双人间',
        'queen': '大床房',
        'suite': '套房'
      };
      return typeMap[type] || type;
    };
    
    // 获取状态类型
    const getStatusType = (status) => {
      const statusMap = {
        'available': 'success',
        'booked': 'warning',
        'occupied': 'danger',
        'maintenance': 'info'
      };
      return statusMap[status] || 'info';
    };
    
    // 获取状态名称
    const getStatusName = (status) => {
      const statusMap = {
        'available': '可用',
        'booked': '已预订',
        'occupied': '已占用',
        'maintenance': '维护中'
      };
      return statusMap[status] || status;
    };
    
    return {
      rooms,
      searchQuery,
      currentPage,
      pageSize,
      total,
      handleCreate,
      handleViewDetails,
      handleEdit,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      getTypeName,
      getStatusType,
      getStatusName
    };
  }
};
</script>

<style scoped>
.room-list-container {
  padding: 20px;
}

.room-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>